// How a user is written wherever the subscription picker shows one. Phone is
// included because two customers sharing a name is ordinary and the name alone
// would leave the operator guessing which row is which.
//
// Guarded like selectableUsers' sort: these fields are non-null on
// UserResponse, but the two functions see exactly the same records, and one of
// them printing "undefined undefined (undefined)" into a live picker while the
// other carefully tolerates the same record is not a defensible split.
export const userLabel = (user) => {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return user.phone ? `${name} (${user.phone})` : name;
};

// The users an operator may attach a subscription to, in the order a person
// reads a list of names.
//
// Staff (`is_admin` / `is_viewer`) are left out: they are colleagues, not
// customers. This is a convenience, not an access control — POST
// /admin/subscriptions still accepts any user id, staff included, and nothing
// should be built on the assumption that it does not.
//
// Deactivated users stay in, tagged by the component. Hiding them would make an
// account the operator knows exists look missing, which reads as a bug in the
// dashboard rather than as a rule.
//
// Sorted by last name then first name with Romanian collation, so Ș and Ț sort
// after S and T instead of falling to the end of the alphabet. `.filter()`
// already returned a fresh array, so sorting it in place cannot touch the
// caller's list.
//
// Lives here rather than in the component because Jest cannot import anything
// whose import graph reaches Radix; a React-free module is what makes these two
// rules testable. It is also the better seam: this decides who is eligible and
// how they are ordered, the component decides only how they are drawn.
export const selectableUsers = (users) =>
  users
    .filter((user) => !user.is_admin && !user.is_viewer)
    .sort(
      (a, b) =>
        // Guarded the same way filterUsers guards its field reads. These are
        // non-null on UserResponse, but an unguarded localeCompare fails harder
        // than a missed match does: it throws inside useMemo and blanks the
        // whole dialog rather than returning the wrong rows.
        (a.last_name || '').localeCompare(b.last_name || '', 'ro') ||
        (a.first_name || '').localeCompare(b.first_name || '', 'ro')
    );
